<template>
  <div class="spreadsheet-importer">
    <div>
      <v-radio-group v-model="source">
        <v-radio value="santander" label="Santander"></v-radio>
        <v-radio value="revolut" label="Revolut"></v-radio>
        <v-radio value="edenred" label="Edenred"></v-radio>
        <v-radio value="santander_pdf" label="Santander PDF parsed"></v-radio>
        <v-radio value="coinbase" label="Coinbase"></v-radio>
      </v-radio-group>
    </div>
    <div>
      <label for="file-input" class="file-label"> Choose Spreadsheet File (CSV or XLSX) </label>
      <input
        id="file-input"
        type="file"
        accept=".csv,.xlsx,.xls"
        @change="handleFileChange"
        class="file-input"
        ref="fileInput"
      />
      <p v-if="loading" class="status">Parsing file...</p>
    </div>

    <div v-if="file && !error" style="margin-top: 10px">
      <v-btn :loading="uploading" flat depressed @click="uploadFile">upload</v-btn>
      <p>folder: {{ folder }}</p>
      <p>fileName: {{ fileName }}</p>
      <p v-if="uploading" class="status">Uploading file...</p>
    </div>
    <p v-if="error" class="error">{{ error }}</p>
  </div>
</template>

<script>
import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import utils from '@/assets/utils/index'
import api from '@/assets/api'

export default {
  name: 'SpreadsheetImporter',
  data() {
    return {
      loading: false,
      uploading: false,
      error: null,
      source: 'santander',

      file: null,
      fileHash: '',
      fileExtension: '',

      data: [],

      keys: {
        date1: ['FECHA OPERACIÓN', 'Started Date'],
      },
      //layout of the excel (columns basically)
      layout: {
        santander: [
          { text: ['fecha operación'], key: 'date2' },
          { text: ['fecha valor'], key: 'date1' },
          { text: ['concepto'], key: 'text' },
          { text: ['importe eur', 'importe'], key: 'amount' },
          { text: ['saldo'], key: 'balance' },
        ],
        santander_pdf: [
          { text: ['page'], key: 'page' },
          { text: ['date2'], key: 'date2' },
          { text: ['date1'], key: 'date1' },
          { text: ['date3'], key: 'date3' },
          { text: ['operation_code'], key: 'operation_code' },
          { text: ['form'], key: 'form' },
          { text: ['text', 'description'], key: 'text' },
          { text: ['amount'], key: 'amount' },
          { text: ['balance'], key: 'balance' },
          { text: ['commission'], key: 'commission' },
        ],
        revolut: [
          { text: ['type'], key: 'revolut.type' },
          { text: ['product'], key: 'revolut.product' },
          { text: ['started date'], key: 'date1' },
          { text: ['completed date'], key: 'date2' },
          { text: ['description'], key: 'text' },
          { text: ['amount'], key: 'amount' },
          { text: ['fee'], key: 'fee' },
          { text: ['currency'], key: 'currency' },
          { text: ['state'], key: 'state' },
          { text: ['balance'], key: 'balance' },
        ],
        edenred: [
          { text: ['fecha'], key: 'date1' },
          { text: ['detalle movimiento'], key: 'text' },
          { text: ['importe'], key: 'amount' },
        ],
        coinbase: [
          { text: ['id'], key: 'id' },
          { text: ['timestamp'], key: 'timestamp' },
          { text: ['transaction type'], key: 'type' },
          { text: ['asset'], key: 'asset' },
          { text: ['quantity transacted'], key: 'quantity' },
          { text: ['price currency'], key: 'currency' },
          { text: ['price at transaction'], key: 'price' },
          { text: ['subtotal'], key: 'subtotal' },
          { text: ['total (inclusive of fees and/or spread)'], key: 'total' },
          { text: ['fees and/or spread'], key: 'fees' },
          { text: ['notes'], key: 'notes' },
        ],
      },
    }
  },
  computed: {
    folder() {
      return `expenses/${this.source}`
    },
    fileName() {
      return this.fileHash + '.' + this.fileExtension
    },
  },
  watch: {
    async file(newValue) {
      console.log('file changed!')
      this.error = undefined
      this.fileName = ''
      if (!newValue) return 'pending'
      const extension = newValue.name.includes('.') ? newValue.name.split('.').pop() : ''
      this.fileExtension = extension
      this.fileHash = await this.sha256(this.file)
    },
  },

  methods: {
    handleFileChange(event) {
      const file = event.target.files?.[0]
      if (!file) return

      this.loading = true
      this.error = null
      this.$refs.fileInput.value = ''

      const fileName = file.name.toLowerCase()
      this.file = file
      this.fileName = fileName

      if (fileName.endsWith('.csv')) {
        this.parseCSV(file)
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        this.parseXLSX(file)
      } else {
        this.error = 'Unsupported file type. Please select a CSV or XLSX file.'
        this.loading = false
      }
    },

    emitOutput(data) {
      this.data = data
      this.$emit('done', data)
    },

    parseCSV(file) {
      Papa.parse(file, {
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            const parsedRows = await this.processRows(results.data)
            this.emitOutput(parsedRows)
          } catch (err) {
            this.error = 'Error processing CSV: ' + err.message
          } finally {
            this.loading = false
          }
        },
        error: (err) => {
          this.error = 'CSV parsing failed: ' + err.message
          this.loading = false
        },
      })
    },

    parseXLSX(file) {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const data = e.target?.result
          if (!data) throw new Error('Empty file data')

          const workbook = XLSX.read(data, { type: 'binary' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]

          const allRows = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
            blankrows: false,
          })

          const parsedRows = await this.processRows(allRows)
          this.emitOutput(parsedRows)
        } catch (err) {
          this.error = 'Error parsing XLSX: ' + (err.message || err)
        } finally {
          this.loading = false
        }
      }

      reader.onerror = () => {
        this.error = 'Failed to read the file.'
        this.loading = false
      }

      reader.readAsBinaryString(file)
    },

    async processRows(allRows) {
      if (allRows.length === 0) {
        throw new Error('File is empty.')
      }

      const source = this.source
      const layout = this.layout[source]
      console.log('layout[0]', layout[0])
      const firstCol = layout[0]

      // Find the row index where column A contains "FECHA OPERACIÓN"
      let headerRowIndex = -1
      for (let i = 0; i < allRows.length; i++) {
        const row = allRows[i]
        const cellA = row && row[0] ? String(row[0]).trim() : ''

        if (firstCol.text.includes(cellA.toLocaleLowerCase().trim())) {
          headerRowIndex = i
          break
        }
      }

      if (headerRowIndex === -1) {
        throw new Error(`Header "${firstCol.text}" not found in column A.`)
      }

      //build the mapping
      const headers = allRows[headerRowIndex].map((h) => h.toLowerCase().trim())
      const mapping = {} // {index: key}

      console.log('headers', headers)

      for (let h = 0; h < headers.length; h++) {
        const header = headers[h]
        const index = layout.findIndex((a) => a.text.includes(header.toLowerCase().trim()))
        if (index !== -1) mapping[index] = layout[index].key
      }
      console.log('mapping', mapping)

      // Data starts on the row AFTER the header
      const dataRows = allRows.slice(headerRowIndex + 1)

      if (dataRows.length === 0) {
        throw new Error('No data rows found after the header.')
      }

      const cleanedRows = dataRows
        .filter(
          (row) =>
            Array.isArray(row) &&
            row.length > 0 &&
            row.some((cell) => cell !== '' && cell !== null && cell !== undefined)
        )
        .map((row) => {
          const row2 = {}
          for (let c = 0; c < row.length; c++) {
            const cell = row[c]
            const k = mapping[c]
            if (k) row2[k] = this.cleanCell(cell)
          }
          row2.source = source
          if (source == 'santander_pdf') row2.source = 'santander'
          return row2
        })
        //further filters
        .filter((item) => {
          if (source == 'santander_pdf') {
            if (item.text.startsWith('S A L D O')) return false
          }
          return true
        })

      // Add hash to each row
      const rowsWithHash = await Promise.all(
        cleanedRows.map(async (row) => {
          if (source == 'santander') {
            row.date1 = this.fixSantanderDate(row.date1)
            row.date2 = this.fixSantanderDate(row.date2)
            row.amount = this.fixDecimals(row.amount)
            row.balance = this.fixDecimals(row.balance)
          }
          if (source == 'revolut') {
            row.amount = parseFloat(row.amount).toFixed(2)
            row.balance = parseFloat(row.balance).toFixed(2)
          }
          if (source == 'edenred') {
            //edenred returns all values as positive for some reason.
            //we expect only "RECARGA" would be positive, although returns would be too...
            row.amount = row.text == 'RECARGA' ? row.amount : -1 * row.amount
          }
          if (source == 'santander_pdf') {
            row.date1 = this.fixSantanderDate(row.date1, '-')
            row.date2 = this.fixSantanderDate(row.date2, '-')
            row.date3 = this.fixSantanderDate(row.date3, '-')
          }
          if (source == 'coinbase') {
            //nothing so far, we handle this separartely
            const currency_fields = ['quantity', 'price', 'subtotal', 'total', 'fees']
            currency_fields.forEach((field) => {
              row[field] = parseFloat(row[field].replace('€', '').trim())
            })
          }
          if (row.text) row.text = row.text.trim()

          //if (row.id) row.hash = row.id //coinbase has reliable id

          if (!row.id) {
            //hash (if no reliable id) -> so far we only have id for coinbase
            const pseudo_id = [
              source,
              row.timestamp || row.date1,
              row.text?.toLowerCase(),
              row.amount,
              row.balance,
              row.currency,
            ].join('|')
            const hash = row.id || (await this.sha256(pseudo_id))
            row.hash = hash
          }
          return row
        })
      )

      return rowsWithHash
    },

    cleanCell(value) {
      if (value === null || value === undefined) return ''
      return String(value).trim()
    },

    fixSantanderDate(dateStr, sep = '/') {
      if (!dateStr) return ''
      let [d, m, y] = dateStr.split(sep).map(Number)
      if (y < 100) y = +`20${y}` //20xx - santander using "19" for 2019
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    },

    fixDecimals(numberStr) {
      if (!numberStr) return undefined
      let x = numberStr.trim().replace(/−/g, '-') // .replace(/\./g, '').replace(',', '.')
      x = this.normalizeNumberString(x)
      const o = Number(parseFloat(x).toFixed(2))
      console.log('numberStr', numberStr, o)
      return o
    },
    /** chatgpt version: */
    normalizeNumberString(str) {
      str = str.trim()

      const lastComma = str.lastIndexOf(',')
      const lastDot = str.lastIndexOf('.')

      let decimalSeparator = null

      if (lastComma > lastDot) decimalSeparator = ','
      else if (lastDot > lastComma) decimalSeparator = '.'

      if (decimalSeparator) {
        const thousandsSeparator = decimalSeparator === ',' ? '.' : ','

        str = str.replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '').replace(decimalSeparator, '.')
      }

      return str
    },

    async sha256(message) {
      return utils.sha256(message)
    },

    /** this will upload the file as-is and the parsed data as .json
     * we use the file hash as the filename for both
     */
    async uploadFile() {
      this.uploading = true

      const folder = this.folder
      if (!folder) return alert('missing folder name. try again')

      //upload original file
      const fileName = this.fileName
      const x = await api.uploadFile(this.file, folder, fileName)
      if (!x.viewUrl) return alert('Error uploading')

      //upload parse data as .json file
      const jsonName = this.fileHash + '.json'
      const jsonFile = new File([JSON.stringify(this.data)], jsonName, {
        type: 'application/json',
      })
      const x2 = await api.uploadFile(jsonFile, folder, jsonName)
      if (!x2.viewUrl) return alert('Error uploading json')
      this.uploading = false

      alert('Uploaded!')
      location.reload()
    },
  },
}
</script>

<style scoped>
.spreadsheet-importer {
  margin: 20px 0;
  text-align: center;
}

.file-label {
  display: inline-block;
  padding: 12px 28px;
  background-color: #007bff;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.file-label:hover {
  background-color: #0056b3;
}

.file-input {
  display: none;
}

.status {
  color: #007bff;
  font-weight: 500;
  margin-top: 12px;
}

.error {
  color: #410007;
  font-weight: 500;
  margin-top: 12px;
}
</style>
